import { useEffect, useRef, useState } from "react";
import { ArrowOutward, Close, Menu, PhoneOutlined } from "@mui/icons-material";
import { Link, NavLink, useLocation } from "react-router-dom";
import { menuItems } from "./NavbarItems";
import Logo from "../../shared/assets/images/Logo.png";
import "./styles.css";

const BOOKING_URL = "https://www.muntra.com/valsta-tandlakarklinik/c/4519?language=sv";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const header = useRef<HTMLElement>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => { if (desktop.matches) setMobileMenuOpen(false); };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
        menuButton.current?.focus();
      }
    };
    const handleOutsideClick = (event: PointerEvent) => {
      if (event.target instanceof Node && !header.current?.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("pointerdown", handleOutsideClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("pointerdown", handleOutsideClick);
    };
  }, [mobileMenuOpen]);

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <header className="clinic-header" ref={header}>
      <div className="clinic-header__utility">
        <div className="container clinic-header__utility-inner">
          <span className="clinic-header__location">Valstavägen 32E · Märsta</span>
          <a href="tel:+46859440060" aria-label="Ring Valsta Tandläkarklinik på 08-594 400 60">
            <PhoneOutlined aria-hidden="true" />
            <span>08-594 400 60</span>
          </a>
        </div>
      </div>

      <div className="container clinic-header__main">
        <Link to="/" className="clinic-header__logo" onClick={closeMenu} aria-label="Valsta Tandläkarklinik – startsida">
          <img src={Logo} alt="Valsta Tandläkarklinik" width="1035" height="282" />
        </Link>

        <nav className="clinic-header__desktop-nav" aria-label="Huvudmeny">
          {menuItems.map((item) => (
            <NavLink key={item.link} to={item.link} end={item.link === "/"}
              className={({ isActive }) => `clinic-header__nav-link${isActive ? " is-active" : ""}`}>
              {item.title}
            </NavLink>
          ))}
        </nav>

        <div className="clinic-header__actions">
          <a className="clinic-header__booking" href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
            aria-label="Boka tid hos Muntra (öppnas i en ny flik)" onClick={closeMenu}>
            Boka tid <ArrowOutward aria-hidden="true" />
          </a>
          <button ref={menuButton} className="clinic-header__menu-toggle" type="button"
            aria-label={mobileMenuOpen ? "Stäng menyn" : "Öppna menyn"}
            aria-expanded={mobileMenuOpen} aria-controls="clinic-mobile-menu"
            onClick={() => setMobileMenuOpen((open) => !open)}>
            {mobileMenuOpen ? <Close aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div id="clinic-mobile-menu" className="clinic-header__mobile-panel">
          <div className="container">
            <nav className="clinic-header__mobile-nav" aria-label="Mobilmeny">
              {menuItems.map((item) => (
                <NavLink key={item.link} to={item.link} end={item.link === "/"} onClick={closeMenu}
                  className={({ isActive }) => `clinic-header__nav-link${isActive ? " is-active" : ""}`}>
                  {item.title}
                </NavLink>
              ))}
            </nav>
            <a className="clinic-header__mobile-phone" href="tel:+46859440060" onClick={closeMenu}>
              <PhoneOutlined aria-hidden="true" /> Ring oss: 08-594 400 60
            </a>
            <p className="clinic-header__mobile-hours">Mån–tors 08–17 · Fre 08–14</p>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
